import React from 'react';
import { Link } from 'react-router-dom';
import {
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import Breadcrumb from '../../components/Navigation/Breadcrumb';

const SUPPORT_EMAIL = 'info@smileconsent.co.uk';

export default function SupportPage() {
  usePageMeta(
    'Support | SmileConsent',
    'Contact SmileConsent for help with your account, billing, or using consent workflows. Email the team or use our contact form.',
    'https://smileconsent.co.uk/support'
  );

  const cards = [
    {
      title: 'Email us',
      body: 'For account questions, technical issues, and general enquiries.',
      action: (
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="inline-flex items-center gap-2 font-semibold text-blue-600 hover:text-blue-800"
        >
          <EnvelopeIcon className="h-5 w-5" />
          {SUPPORT_EMAIL}
        </a>
      ),
      icon: EnvelopeIcon,
    },
    {
      title: 'Contact form',
      body: 'Prefer a structured message? Send details through our contact page.',
      action: (
        <Link to="/contact" className="inline-flex items-center gap-2 font-semibold text-blue-600 hover:text-blue-800">
          <ChatBubbleLeftRightIcon className="h-5 w-5" />
          Go to contact
        </Link>
      ),
      icon: ChatBubbleLeftRightIcon,
    },
    {
      title: 'Book a demo',
      body: 'New to SmileConsent? Arrange a short walkthrough with the team.',
      action: (
        <Link to="/book-demo" className="inline-flex items-center gap-2 font-semibold text-blue-600 hover:text-blue-800">
          <CalendarIcon className="h-5 w-5" />
          Book a demo
        </Link>
      ),
      icon: CalendarIcon,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Breadcrumb
          items={[
            { name: 'Home', href: '/' },
            { name: 'Support', href: '/support' },
          ]}
        />
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-20">
        <header className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-100 text-blue-700 mb-4">
            <QuestionMarkCircleIcon className="h-8 w-8" aria-hidden />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">Support</h1>
          <p className="mt-4 text-lg text-gray-600 max-w-xl mx-auto">
            We&apos;re here to help UK practices get the most from SmileConsent. Choose the option that suits you
            best.
          </p>
        </header>

        <div className="space-y-6">
          {cards.map(({ title, body, action, icon: Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8 flex gap-4"
            >
              <div className="shrink-0 w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                <Icon className="h-6 w-6 text-slate-700" aria-hidden />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">{title}</h2>
                <p className="mt-1 text-gray-600 text-sm sm:text-base">{body}</p>
                <div className="mt-4">{action}</div>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-10 text-center text-sm text-gray-500">
          Logged-in practice users: you can also use in-app settings and billing for subscription questions.
        </p>
      </div>
    </div>
  );
}
